import { DateTime } from 'luxon';
import { getFiscalStartMonthFromYearEnd } from 'utils/fiscalYear';
import {
  DateTimeInput,
  emptyDateRange,
  nowDateTime,
  parseInZone,
  resolveDateTimeZone,
  toISODateRange,
} from 'utils/dateParsing';

/** Start/end date inputs accepted by range helpers. */
export type DateRangeInput = [DateTimeInput, DateTimeInput];

const DEFAULT_FISCAL_YEAR_END_MONTH = 12;
const ALL_TIME_START_YEAR = 1950;

const buildDateTimeInZone = (
  timezone: string | null | undefined,
  year: number,
  month: number,
  day: number
) =>
  DateTime.fromObject(
    { year, month, day, hour: 0, minute: 0, second: 0, millisecond: 0 },
    { zone: resolveDateTimeZone(timezone) }
  );

const getFiscalYearStart = (
  referenceDate: DateTime,
  fiscalYearEndMonth: number,
  timezone?: string | null
) => {
  const fiscalStartMonth = getFiscalStartMonthFromYearEnd(fiscalYearEndMonth);
  const year =
    referenceDate.month >= fiscalStartMonth
      ? referenceDate.year
      : referenceDate.year - 1;

  return buildDateTimeInZone(timezone, year, fiscalStartMonth, 1);
};

/**
 * Expands a start/end picker range into inclusive `YYYY-MM-DD` date tokens.
 * Use when each calendar day in a selected range needs to be represented.
 */
export const getInclusiveDateRange = (
  range: DateRangeInput,
  timezone?: string | null
): string[] => {
  const [start, rawEnd] = range;
  if (!start) return [];

  const zone = resolveDateTimeZone(timezone);
  const startDay = parseInZone(start, zone).startOf('day');
  const endDay = parseInZone(rawEnd || start, zone).startOf('day');

  if (!startDay.isValid || !endDay.isValid || endDay < startDay) return [];

  const days: string[] = [];
  for (
    let current = startDay;
    current <= endDay;
    current = current.plus({ days: 1 })
  ) {
    days.push(current.toISODate());
  }

  return days;
};

/**
 * Returns the first and last calendar dates for the month containing `value`.
 * Use for monthly reports and month picker normalization.
 */
export const getMonthRange = (
  value: DateTimeInput,
  timezone?: string | null
): [string, string] => {
  const parsed = parseInZone(value, timezone);

  return parsed.isValid
    ? toISODateRange(parsed.startOf('month'), parsed.endOf('month'))
    : emptyDateRange();
};

/**
 * Returns the current month range in the site/default timezone.
 */
export const getCurrentMonthRange = (timezone?: string | null) =>
  getMonthRange(nowDateTime(timezone), timezone);

/**
 * Returns the fiscal year range containing `referenceDate`. Use for fiscal-year
 * dashboards and report filters.
 */
export const getFiscalYearRange = ({
  fiscalYearEndMonth = DEFAULT_FISCAL_YEAR_END_MONTH,
  referenceDate = nowDateTime(),
  timezone,
}: {
  fiscalYearEndMonth?: number;
  referenceDate?: DateTimeInput;
  timezone?: string | null;
} = {}): [string, string] => {
  const reference = parseInZone(referenceDate, timezone);
  if (!reference.isValid) return emptyDateRange();

  const start = getFiscalYearStart(reference, fiscalYearEndMonth, timezone);
  const end = start.plus({ years: 1 }).minus({ days: 1 }).endOf('day');

  return toISODateRange(start, end);
};

/**
 * Builds the preset ranges used by date pickers: YTD, last month, last quarter,
 * last fiscal year, and all time.
 *
 * Use for shared date picker preset menus so all report/dashboard filters derive
 * ranges with the same timezone and fiscal-year rules.
 */
export const getDatePickerPresetRanges = ({
  fiscalYearEndMonth = DEFAULT_FISCAL_YEAR_END_MONTH,
  timezone,
  referenceDate = nowDateTime(timezone),
}: {
  fiscalYearEndMonth?: number;
  timezone?: string | null;
  referenceDate?: DateTimeInput;
} = {}) => {
  const today = parseInZone(referenceDate, timezone);

  if (!today.isValid) {
    return {
      yearToDate: emptyDateRange(),
      lastMonth: emptyDateRange(),
      lastQuarter: emptyDateRange(),
      lastFiscalYear: emptyDateRange(),
      allTime: emptyDateRange(),
    };
  }

  const lastMonth = today.minus({ months: 1 });
  const currentQuarter = Math.floor((today.month - 1) / 3) + 1;
  const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
  const lastQuarterYear = currentQuarter === 1 ? today.year - 1 : today.year;
  const lastQuarterStart = buildDateTimeInZone(
    timezone,
    lastQuarterYear,
    (lastQuarter - 1) * 3 + 1,
    1
  );
  const currentFiscalYearStart = getFiscalYearStart(
    today,
    fiscalYearEndMonth,
    timezone
  );
  const lastFiscalYearStart = currentFiscalYearStart.minus({ years: 1 });
  const allTimeStart = buildDateTimeInZone(timezone, ALL_TIME_START_YEAR, 1, 1);

  return {
    yearToDate: toISODateRange(today.startOf('year'), today),
    lastMonth: toISODateRange(
      lastMonth.startOf('month'),
      lastMonth.endOf('month')
    ),
    lastQuarter: toISODateRange(
      lastQuarterStart,
      lastQuarterStart.plus({ months: 3 }).minus({ days: 1 }).endOf('day')
    ),
    lastFiscalYear: toISODateRange(
      lastFiscalYearStart,
      currentFiscalYearStart.minus({ days: 1 }).endOf('day')
    ),
    allTime: toISODateRange(allTimeStart, today),
  };
};
